import {
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  Activity,
  ArrowRightCircle,
  ChevronDown,
  Check,
  FolderKanban,
  Globe2,
  KeyRound,
  LogOut,
  Mail,
  Menu,
  Play,
  Plus,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";
import type {
  Approval,
  AuthenticatedUser,
  DashboardInventory,
  DashboardPayload,
  ExecutionJob,
  Opportunity,
  Site,
  SiteTargetAudience,
} from "@shared/types";
import { fetchAuthed, fetchBootstrap } from "./lib/api";
import { getBrowserSupabase, getInitialSession } from "./lib/supabase";
import { IconActionButton } from "./components/IconActionButton";
import { StatusBadge } from "./components/StatusBadge";
import { TargetAudienceEditor } from "./components/TargetAudienceEditor";
import {
  isEmbeddedToolReadyMessage,
  postEmbeddedToolSiteSelection,
} from "./embedded-tools";
import { moduleItems, type ModuleItem, type TabKey } from "./module-items";
import {
  ALL_SITES_KEY,
  filterBySiteId,
  filterInventoryBySiteId,
  matchesSiteQuery,
} from "./site-scope";

type BootstrapConfig = Awaited<ReturnType<typeof fetchBootstrap>>;

const externalModuleItems = moduleItems.filter(
  (item) => item.kind === "external"
);
const embeddedModuleItems = moduleItems.filter(
  (item) => item.kind === "embedded"
);
const internalModuleItems = moduleItems.filter(
  (item) => item.kind === "internal"
);
const openseoDirectExternalModuleKeys = [
  "keyword-research",
  "domain-insights",
  "backlinks",
] as const;
const siteAuditModuleKey = "openseo-site-audits";
const secondaryExternalModuleKeys = [
  "analytics-tracking",
  "event-tracking",
  "conversion-tracking",
  "keyword-tracking",
] as const;
const externalModuleItemMap = new Map(
  externalModuleItems.map((item) => [item.key, item])
);

function getDefaultModuleKey(tab: TabKey): string | null {
  return (
    moduleItems.find((item) => item.kind === "internal" && item.tab === tab)
      ?.key ?? null
  );
}

function MetricTile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  icon: typeof Activity;
}) {
  const isLongValue = typeof value === "string" || `${value}`.length > 8;

  return (
    <div className="metric-tile">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.24em] text-neutral-500">
          {label}
        </span>
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <p
        className={`mt-4 font-semibold text-white ${isLongValue ? "break-all text-xl leading-snug sm:text-2xl" : "text-3xl"}`}
      >
        {value}
      </p>
    </div>
  );
}

function AuthView({
  config,
  onAuthenticated,
}: {
  config: BootstrapConfig;
  onAuthenticated: (session: Session) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = getBrowserSupabase(config);

  async function login(mode: "password" | "magic_link") {
    setBusy(true);
    setError(null);

    try {
      if (mode === "password") {
        const { data, error: authError } = await client.auth.signInWithPassword(
          {
            email,
            password,
          }
        );
        if (authError) {
          throw authError;
        }
        if (data.session) {
          onAuthenticated(data.session);
        }
      } else {
        const { error: authError } = await client.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });
        if (authError) {
          throw authError;
        }
      }
    } catch (authFailure) {
      setError(
        authFailure instanceof Error
          ? authFailure.message
          : "Authentication failed"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell items-center justify-center">
      <div className="glass-panel w-full max-w-xl overflow-hidden p-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
              Approve Then Apply
            </p>
            <h1 className="mt-3 text-4xl font-semibold text-white">
              {config.appName}
            </h1>
            <p className="mt-3 max-w-lg text-sm leading-6 text-neutral-400">
              Internal SEO operations console for ingestion, approvals, and
              production execution.
            </p>
          </div>
          <div className="rounded-3xl border border-lime-300/25 bg-lime-300/10 p-3 text-lime-200">
            <ShieldCheck className="h-7 w-7" />
          </div>
        </div>

        <div className="space-y-5">
          <label className="field-shell">
            <span className="field-label">Email</span>
            <span className="field-addon">
              <Mail className="h-4 w-4" />
            </span>
            <input
              className="field-input"
              placeholder="you@yourdomain.com"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>

          <label className="field-shell">
            <span className="field-label">Password</span>
            <span className="field-addon">
              <KeyRound className="h-4 w-4" />
            </span>
            <input
              className="field-input"
              placeholder="Enter your password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
        </div>

        {error ? (
          <div className="mt-5 rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-lime-300/25 bg-lime-300/10 px-4 py-3 text-sm font-medium text-lime-100 transition hover:bg-lime-300/15 disabled:opacity-50"
            disabled={busy || !email || !password}
            onClick={() => login("password")}
          >
            <ShieldCheck className="h-4 w-4" />
            Sign in
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-50"
            disabled={busy || !email}
            onClick={() => login("magic_link")}
          >
            <ArrowRightCircle className="h-4 w-4" />
            Email magic link
          </button>
        </div>
      </div>
    </div>
  );
}

function AppShell({
  session,
  currentUser,
  sites,
  inventory,
  dashboard,
  refreshing,
  onRefresh,
  onCreateSite,
  onRunSiteAssessment,
  onUpdateTargetAudience,
  onGenerateAction,
  onApprovalDecision,
  onRunExecution,
  onPrepareKeywordDensityLaunch,
  onLogout,
}: {
  session: Session;
  currentUser: AuthenticatedUser;
  sites: Site[];
  inventory: DashboardInventory;
  dashboard: DashboardPayload;
  refreshing: boolean;
  onRefresh: () => void;
  onCreateSite: (payload: {
    name: string;
    primaryUrl: string;
  }) => Promise<Site>;
  onRunSiteAssessment: (siteId: string) => Promise<void>;
  onUpdateTargetAudience: (
    siteId: string,
    targetAudience: SiteTargetAudience
  ) => Promise<void>;
  onGenerateAction: (opportunityId: string) => void;
  onApprovalDecision: (approvalId: string, mode: "approve" | "reject") => void;
  onRunExecution: (actionPlanId: string) => void;
  onPrepareKeywordDensityLaunch: (siteId: string | null) => Promise<{
    launchUrl: string;
    targetOrigin: string;
    expiresAt: string;
  }>;
  onLogout: () => void;
}) {
  const initialTab: TabKey = "dashboard";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [selectedModuleKey, setSelectedModuleKey] = useState<string | null>(
    getDefaultModuleKey(initialTab)
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [siteSearch, setSiteSearch] = useState("");
  const [siteName, setSiteName] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [submittingSite, setSubmittingSite] = useState(false);
  const [assessmentSiteId, setAssessmentSiteId] = useState<string | null>(null);
  const [keywordDensityLaunch, setKeywordDensityLaunch] = useState<{
    launchUrl: string;
    targetOrigin: string;
    expiresAt: string;
  } | null>(null);
  const [keywordDensityLoading, setKeywordDensityLoading] = useState(false);
  const [keywordDensityError, setKeywordDensityError] = useState<string | null>(
    null
  );
  const [keywordDensityFrameReady, setKeywordDensityFrameReady] =
    useState(false);
  const [siteSelectorOpen, setSiteSelectorOpen] = useState(false);
  const [siteCreationOpen, setSiteCreationOpen] = useState(false);
  const deferredSearch = useDeferredValue(search.trim().toLowerCase());
  const [activeSiteId, setActiveSiteId] = useState<string>(ALL_SITES_KEY);
  const siteSelectorRef = useRef<HTMLDivElement | null>(null);
  const embeddedFrameRef = useRef<HTMLIFrameElement | null>(null);
  const activeSiteIdRef = useRef(activeSiteId);
  const visibleTabs: TabKey[] = currentUser.isInternal
    ? ["dashboard", "opportunities", "approvals", "executions"]
    : ["dashboard", "approvals", "executions"];
  const scopedOpportunities = filterBySiteId(
    dashboard.opportunities,
    activeSiteId
  );
  const scopedApprovals = filterBySiteId(dashboard.approvals, activeSiteId);
  const scopedExecutions = filterBySiteId(dashboard.executions, activeSiteId);
  const scopedInventory = filterInventoryBySiteId(inventory, activeSiteId);
  const opportunityResults = scopedOpportunities.filter((item) => {
    if (!deferredSearch) {
      return true;
    }
    return `${item.title} ${item.summary}`
      .toLowerCase()
      .includes(deferredSearch);
  });
  const approvalResults = scopedApprovals.filter((item) => {
    if (!deferredSearch) {
      return true;
    }
    return `${item.stage} ${item.diff_summary}`
      .toLowerCase()
      .includes(deferredSearch);
  });
  const executionResults = scopedExecutions.filter((item) => {
    if (!deferredSearch) {
      return true;
    }
    return `${item.provider} ${item.status}`
      .toLowerCase()
      .includes(deferredSearch);
  });
  const selectedModule =
    moduleItems.find((item) => item.key === selectedModuleKey) ?? null;
  const selectedEmbeddedModule =
    selectedModule?.kind === "embedded" ? selectedModule : null;
  const selectedSite =
    activeSiteId === ALL_SITES_KEY
      ? null
      : (sites.find((site) => site.id === activeSiteId) ?? null);
  const selectedSiteLabel = selectedSite?.name ?? "All sites";
  const filteredSites = sites.filter((site) =>
    matchesSiteQuery(site, siteSearch)
  );
  const assessmentTargetSite =
    activeSiteId === ALL_SITES_KEY
      ? sites.length === 1
        ? sites[0]
        : null
      : selectedSite;

  useEffect(() => {
    activeSiteIdRef.current = activeSiteId;
  }, [activeSiteId]);

  useEffect(() => {
    if (
      !selectedEmbeddedModule ||
      selectedEmbeddedModule.key !== "keyword-density"
    ) {
      setKeywordDensityLaunch(null);
      setKeywordDensityLoading(false);
      setKeywordDensityError(null);
      setKeywordDensityFrameReady(false);
      return;
    }

    let cancelled = false;
    setKeywordDensityLoading(true);
    setKeywordDensityError(null);
    setKeywordDensityFrameReady(false);

    onPrepareKeywordDensityLaunch(
      activeSiteIdRef.current === ALL_SITES_KEY ? null : activeSiteIdRef.current
    )
      .then((launch) => {
        if (!cancelled) {
          setKeywordDensityLaunch(launch);
        }
      })
      .catch((launchError) => {
        if (!cancelled) {
          setKeywordDensityError(
            launchError instanceof Error
              ? launchError.message
              : "Unable to launch Keyword Density"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setKeywordDensityLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [onPrepareKeywordDensityLaunch, selectedEmbeddedModule]);

  useEffect(() => {
    if (
      !selectedEmbeddedModule ||
      selectedEmbeddedModule.key !== "keyword-density" ||
      !keywordDensityLaunch
    ) {
      return;
    }

    const targetOrigin = keywordDensityLaunch.targetOrigin;

    function handleEmbeddedReady(event: MessageEvent<unknown>) {
      if (
        event.origin !== targetOrigin ||
        !isEmbeddedToolReadyMessage(event.data)
      ) {
        return;
      }

      setKeywordDensityFrameReady(true);
    }

    window.addEventListener("message", handleEmbeddedReady);
    return () => {
      window.removeEventListener("message", handleEmbeddedReady);
    };
  }, [keywordDensityLaunch, selectedEmbeddedModule]);

  useEffect(() => {
    if (
      !selectedEmbeddedModule ||
      selectedEmbeddedModule.key !== "keyword-density" ||
      !keywordDensityLaunch ||
      !keywordDensityFrameReady ||
      !embeddedFrameRef.current?.contentWindow
    ) {
      return;
    }

    postEmbeddedToolSiteSelection(
      embeddedFrameRef.current.contentWindow,
      keywordDensityLaunch.targetOrigin,
      selectedSite,
      selectedSiteLabel
    );
  }, [
    keywordDensityFrameReady,
    keywordDensityLaunch,
    selectedEmbeddedModule,
    selectedSite,
    selectedSiteLabel,
  ]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!siteSelectorOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (
        siteSelectorRef.current &&
        !siteSelectorRef.current.contains(event.target as Node)
      ) {
        setSiteSelectorOpen(false);
        setSiteSearch("");
        setSiteCreationOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSiteSelectorOpen(false);
        setSiteSearch("");
        setSiteCreationOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [siteSelectorOpen]);

  function openModule(item: ModuleItem) {
    setSelectedModuleKey(item.key);

    if (item.kind === "external") {
      window.open(item.href, "_blank", "noopener,noreferrer");
      setMobileMenuOpen(false);
      return;
    }

    if (item.kind === "embedded") {
      startTransition(() => {
        setActiveTab("dashboard");
      });
      setMobileMenuOpen(false);
      return;
    }

    startTransition(() => {
      setActiveTab(item.tab);
    });
    setMobileMenuOpen(false);
  }

  function selectSite(siteId: string) {
    setActiveSiteId(siteId);
    setSiteSelectorOpen(false);
    setSiteSearch("");
    setSiteCreationOpen(false);
  }

  function openCreateSiteForm() {
    setSiteCreationOpen(true);
    if (!siteName.trim() && siteSearch.trim()) {
      setSiteName(siteSearch.trim());
    }
  }

  function handleTabChange(tab: TabKey) {
    startTransition(() => {
      setActiveTab(tab);
      setSelectedModuleKey(getDefaultModuleKey(tab));
    });
    setMobileMenuOpen(false);
  }

  async function handleRunAssessment(siteId: string) {
    setAssessmentSiteId(siteId);
    try {
      await onRunSiteAssessment(siteId);
    } finally {
      setAssessmentSiteId(null);
    }
  }

  async function handleCreateSite() {
    if (!siteName.trim() || !siteUrl.trim()) {
      return;
    }

    setSubmittingSite(true);
    try {
      const site = await onCreateSite({
        name: siteName.trim(),
        primaryUrl: siteUrl.trim(),
      });
      setSiteName("");
      setSiteUrl("");
      setSiteSearch("");
      setSiteSelectorOpen(false);
      setSiteCreationOpen(false);
      setActiveSiteId(site.id);
      setSelectedModuleKey("content");
      setActiveTab("opportunities");
      await handleRunAssessment(site.id);
    } finally {
      setSubmittingSite(false);
    }
  }

  return (
    <div className="app-shell">
      {mobileMenuOpen ? (
        <button
          type="button"
          className="mobile-nav-backdrop"
          aria-label="Close module menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      ) : null}

      <div className="workspace-shell">
        <aside
          className={`workspace-rail ${mobileMenuOpen ? "workspace-rail-open" : ""}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-lime-300/80">
                Menu
              </p>
              <h2 className="mt-2 text-lg font-semibold text-white">
                Autonomous SEO Ops
              </h2>
            </div>
            <button
              type="button"
              className="module-menu-button lg:hidden"
              aria-label="Close module menu"
              onClick={() => setMobileMenuOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={siteSelectorRef} className="mt-4">
            <p className="rail-section-label">Sites</p>
            <div className="relative mt-1.5">
              <button
                type="button"
                className={`site-selector-trigger ${siteSelectorOpen ? "site-selector-trigger-open" : ""}`}
                aria-expanded={siteSelectorOpen}
                onClick={() => {
                  setSiteSelectorOpen((current) => !current);
                  setSiteCreationOpen(false);
                  setSiteSearch("");
                }}
              >
                <span className="site-selector-trigger-text">
                  {selectedSiteLabel}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition ${siteSelectorOpen ? "rotate-180" : ""}`}
                />
              </button>

              {siteSelectorOpen ? (
                <div className="site-selector-panel">
                  <label className="field-shell site-selector-search-shell">
                    <span className="field-label">Search sites</span>
                    <span className="field-addon">
                      <Search className="h-4 w-4" />
                    </span>
                    <input
                      className="field-input"
                      placeholder="Type to search"
                      value={siteSearch}
                      onChange={(event) => setSiteSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter") {
                          return;
                        }

                        event.preventDefault();

                        if (filteredSites[0]) {
                          selectSite(filteredSites[0].id);
                          return;
                        }

                        if (currentUser.isInternal) {
                          openCreateSiteForm();
                        }
                      }}
                    />
                  </label>

                  <div className="site-selector-options">
                    <button
                      type="button"
                      className={`site-selector-option ${activeSiteId === ALL_SITES_KEY ? "site-selector-option-active" : ""}`}
                      onClick={() => selectSite(ALL_SITES_KEY)}
                    >
                      <span>All sites</span>
                      {activeSiteId === ALL_SITES_KEY ? (
                        <Check className="h-4 w-4" />
                      ) : null}
                    </button>

                    {filteredSites.map((site) => (
                      <button
                        key={site.id}
                        type="button"
                        className={`site-selector-option ${activeSiteId === site.id ? "site-selector-option-active" : ""}`}
                        onClick={() => selectSite(site.id)}
                      >
                        <span className="truncate">{site.name}</span>
                        {activeSiteId === site.id ? (
                          <Check className="h-4 w-4" />
                        ) : null}
                      </button>
                    ))}

                    {filteredSites.length === 0 ? (
                      <div className="site-selector-empty">
                        No sites match the current search.
                      </div>
                    ) : null}
                  </div>

                  {currentUser.isInternal ? (
                    <div className="mt-3 border-t border-white/6 pt-3">
                      <button
                        type="button"
                        className="site-selector-add"
                        onClick={openCreateSiteForm}
                      >
                        <Plus className="h-4 w-4" />
                        Add New Site
                      </button>

                      {siteCreationOpen ? (
                        <form
                          className="mt-3 space-y-3"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void handleCreateSite();
                          }}
                        >
                          <label className="field-shell site-selector-search-shell">
                            <span className="field-label">Site Name</span>
                            <span className="field-addon">
                              <FolderKanban className="h-4 w-4" />
                            </span>
                            <input
                              className="field-input"
                              placeholder="The Portland Company"
                              value={siteName}
                              onChange={(event) =>
                                setSiteName(event.target.value)
                              }
                            />
                          </label>

                          <label className="field-shell site-selector-search-shell">
                            <span className="field-label">Primary URL</span>
                            <span className="field-addon">
                              <Globe2 className="h-4 w-4" />
                            </span>
                            <input
                              className="field-input"
                              placeholder="https://example.com"
                              value={siteUrl}
                              onChange={(event) =>
                                setSiteUrl(event.target.value)
                              }
                            />
                          </label>

                          <button
                            type="submit"
                            className="site-selector-create"
                            disabled={
                              submittingSite ||
                              !siteName.trim() ||
                              !siteUrl.trim()
                            }
                          >
                            <Plus className="h-4 w-4" />
                            {submittingSite
                              ? "Adding site..."
                              : "Add Site and Run Assessment"}
                          </button>
                        </form>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <nav className="mt-4 flex-1 space-y-3">
            <div>
              <p className="rail-section-label">Workspace</p>
              <div className="mt-1.5 space-y-1">
                {internalModuleItems.map((item) => {
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`rail-link group ${selectedModuleKey === item.key ? "rail-link-active" : ""}`}
                      onClick={() => openModule(item)}
                    >
                      <span className="rail-link-icon">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="rail-link-title">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="rail-section-label">External tools</p>
              <div className="mt-1.5 space-y-1">
                {openseoDirectExternalModuleKeys.map((key) => {
                  const item = externalModuleItemMap.get(key);
                  if (!item) {
                    return null;
                  }

                  const Icon = item.icon;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`rail-link group ${selectedModuleKey === item.key ? "rail-link-active" : ""}`}
                      onClick={() => openModule(item)}
                    >
                      <span className="rail-link-icon">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="rail-link-title">{item.label}</span>
                    </button>
                  );
                })}

                {(() => {
                  const siteAuditItem =
                    externalModuleItemMap.get(siteAuditModuleKey);
                  if (!siteAuditItem) {
                    return null;
                  }

                  const AuditIcon = Search;

                  return (
                    <div className="rail-group">
                      <div
                        className={`rail-group-header ${selectedModuleKey === siteAuditItem.key ? "rail-group-header-active" : ""}`}
                      >
                        <span className="rail-link-icon">
                          <AuditIcon className="h-4 w-4" />
                        </span>
                        <span className="rail-link-title">Site Audits</span>
                      </div>

                      <button
                        type="button"
                        className={`rail-sublink ${selectedModuleKey === siteAuditItem.key ? "rail-sublink-active" : ""}`}
                        onClick={() => openModule(siteAuditItem)}
                      >
                        <span className="rail-sublink-marker" />
                        <span className="rail-sublink-title">
                          {siteAuditItem.label}
                        </span>
                      </button>
                    </div>
                  );
                })()}

                <div className="pt-2">
                  <p className="rail-section-label">Integrated tools</p>
                  <div className="mt-1.5 space-y-1">
                    {embeddedModuleItems.map((item) => {
                      const Icon = item.icon;

                      return (
                        <button
                          key={item.key}
                          type="button"
                          className={`rail-link group ${selectedModuleKey === item.key ? "rail-link-active" : ""}`}
                          onClick={() => openModule(item)}
                        >
                          <span className="rail-link-icon">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="rail-link-title">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2">
                  <p className="rail-section-label">Connected tools</p>
                  <div className="mt-1.5 space-y-1">
                    {secondaryExternalModuleKeys.map((key) => {
                      const item = externalModuleItemMap.get(key);
                      if (!item) {
                        return null;
                      }

                      const Icon = item.icon;

                      return (
                        <button
                          key={item.key}
                          type="button"
                          className={`rail-link group ${selectedModuleKey === item.key ? "rail-link-active" : ""}`}
                          onClick={() => openModule(item)}
                        >
                          <span className="rail-link-icon">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="rail-link-title">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <div className="workspace-rail-footer">
            <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
              Signed in
            </p>
            <p className="mt-2 truncate text-sm text-white">
              {session.user.email ?? "unknown"}
            </p>
            <p className="mt-2 text-[11px] text-neutral-500">
              Embedded tools stay in the workspace. External tools open in a new
              tab.
            </p>
          </div>
        </aside>

        <div
          className={`workspace-main ${mobileMenuOpen ? "workspace-main-shifted" : ""}`}
        >
          <WorkspacePageHero
            activeTab={activeTab}
            currentUser={currentUser}
            selectedModule={selectedModule}
            selectedSite={selectedSite}
            selectedSiteLabel={selectedSiteLabel}
            scopedInventory={scopedInventory}
            scopedOpportunities={scopedOpportunities}
            opportunityResults={opportunityResults}
            scopedApprovals={scopedApprovals}
            scopedExecutions={scopedExecutions}
            sessionEmail={session.user.email ?? "unknown"}
            refreshing={refreshing}
            mobileMenuOpen={mobileMenuOpen}
            onOpenMobileMenu={() => setMobileMenuOpen(true)}
            onRefresh={onRefresh}
            onLogout={onLogout}
          />

          {selectedEmbeddedModule?.key === "keyword-density" ? (
            <div className="mt-8 flex flex-col gap-4">
              <p className="text-xs uppercase tracking-[0.24em] text-neutral-500">
                Integrated tool
              </p>
              <div className="glass-panel overflow-hidden p-4 sm:p-5">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                      Embedded analyzer
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      Keyword Density
                    </p>
                    <p className="mt-2 text-sm leading-6 text-neutral-400">
                      The analyzer keeps its own subdomain session and stays
                      aligned to the active site selection in this workspace.
                    </p>
                  </div>
                  <a
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm font-medium text-white transition hover:border-white/15 hover:bg-white/[0.06]"
                    href={selectedEmbeddedModule.href}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <ArrowRightCircle className="h-4 w-4" />
                    Open in new tab
                  </a>
                </div>

                {keywordDensityError ? (
                  <div className="rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    {keywordDensityError}
                  </div>
                ) : null}

                {keywordDensityLoading ? (
                  <div className="embedded-tool-shell items-center justify-center text-sm text-neutral-400">
                    Preparing the Keyword Density analyzer…
                  </div>
                ) : keywordDensityLaunch ? (
                  <iframe
                    ref={embeddedFrameRef}
                    className="embedded-tool-frame"
                    src={keywordDensityLaunch.launchUrl}
                    title="Keyword Density"
                    onLoad={() => {
                      setKeywordDensityFrameReady(false);
                    }}
                  />
                ) : (
                  <div className="embedded-tool-shell items-center justify-center text-sm text-neutral-400">
                    No launch session is available for the embedded analyzer.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-8 flex flex-col gap-4">
              <p className="text-xs uppercase tracking-[0.24em] text-neutral-500">
                Workflow views
              </p>
              <div className="flex flex-wrap gap-2">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`folder-tab ${activeTab === tab ? "folder-tab-active" : ""}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <WorkspacePageContent
                activeTab={activeTab}
                currentUser={currentUser}
                selectedModule={selectedModule}
                selectedSite={selectedSite}
                selectedSiteLabel={selectedSiteLabel}
                search={search}
                onSearchChange={(value) => setSearch(value)}
                assessmentTargetSite={assessmentTargetSite}
                assessmentSiteId={assessmentSiteId}
                inventory={scopedInventory}
                opportunityResults={opportunityResults}
                scopedApprovals={scopedApprovals}
                approvalResults={approvalResults}
                scopedExecutions={scopedExecutions}
                executionResults={executionResults}
                onRunSiteAssessment={(siteId) =>
                  void handleRunAssessment(siteId)
                }
                onUpdateTargetAudience={onUpdateTargetAudience}
                onGenerateAction={onGenerateAction}
                onApprovalDecision={onApprovalDecision}
                onRunExecution={onRunExecution}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type WorkspacePageHeroProps = {
  activeTab: TabKey;
  currentUser: AuthenticatedUser;
  selectedModule: ModuleItem | null;
  selectedSite: Site | null;
  selectedSiteLabel: string;
  scopedInventory: DashboardInventory;
  scopedOpportunities: Opportunity[];
  opportunityResults: Opportunity[];
  scopedApprovals: Approval[];
  scopedExecutions: ExecutionJob[];
  sessionEmail: string;
  refreshing: boolean;
  mobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  onRefresh: () => void;
  onLogout: () => void;
};

type WorkspacePageContentProps = {
  activeTab: TabKey;
  currentUser: AuthenticatedUser;
  selectedModule: ModuleItem | null;
  selectedSite: Site | null;
  selectedSiteLabel: string;
  search: string;
  onSearchChange: (value: string) => void;
  assessmentTargetSite: Site | null;
  assessmentSiteId: string | null;
  inventory: DashboardInventory;
  opportunityResults: Opportunity[];
  scopedApprovals: Approval[];
  approvalResults: Approval[];
  scopedExecutions: ExecutionJob[];
  executionResults: ExecutionJob[];
  onRunSiteAssessment: (siteId: string) => void;
  onUpdateTargetAudience: (
    siteId: string,
    targetAudience: SiteTargetAudience
  ) => Promise<void>;
  onGenerateAction: (opportunityId: string) => void;
  onApprovalDecision: (approvalId: string, mode: "approve" | "reject") => void;
  onRunExecution: (actionPlanId: string) => void;
};

function QueueSearchField({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field-shell max-w-xl grow">
      <span className="field-label">Search</span>
      <span className="field-addon">
        <Search className="h-4 w-4" />
      </span>
      <input
        className="field-input"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function WorkspacePageHero({
  activeTab,
  currentUser,
  selectedModule,
  selectedSite,
  selectedSiteLabel,
  scopedInventory,
  scopedOpportunities,
  opportunityResults,
  scopedApprovals,
  scopedExecutions,
  sessionEmail,
  refreshing,
  mobileMenuOpen,
  onOpenMobileMenu,
  onRefresh,
  onLogout,
}: WorkspacePageHeroProps) {
  const organizationCount = scopedInventory.organizations.length;
  const projectCount = scopedInventory.organizations.reduce(
    (total, organization) => total + organization.projects.length,
    0
  );
  const highRiskOpportunityCount = scopedOpportunities.filter(
    (opportunity) => opportunity.risk_level === "high"
  ).length;
  const highScoreOpportunityCount = scopedOpportunities.filter(
    (opportunity) => opportunity.score >= 40
  ).length;
  const pendingApprovalCount = scopedApprovals.filter(
    (approval) => approval.status === "pending"
  ).length;
  const approvedApprovalCount = scopedApprovals.filter(
    (approval) => approval.status === "approved"
  ).length;
  const rejectedApprovalCount = scopedApprovals.filter(
    (approval) => approval.status === "rejected"
  ).length;
  const runningExecutionCount = scopedExecutions.filter(
    (execution) => execution.status === "running"
  ).length;
  const verifyingExecutionCount = scopedExecutions.filter(
    (execution) => execution.status === "verifying"
  ).length;
  const failedExecutionCount = scopedExecutions.filter(
    (execution) => execution.status === "failed"
  ).length;
  const succeededExecutionCount = scopedExecutions.filter(
    (execution) => execution.status === "succeeded"
  ).length;
  const spotlightIcon =
    selectedModule?.icon ??
    (activeTab === "dashboard"
      ? FolderKanban
      : activeTab === "approvals"
        ? ShieldCheck
        : activeTab === "executions"
          ? Play
          : Sparkles);
  const SpotlightIcon = spotlightIcon;

  if (selectedModule?.kind === "embedded") {
    return (
      <header className="glass-panel overflow-hidden p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
              Embedded SEO Tool
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-white">
                {selectedModule.label}
              </h1>
              <StatusBadge value={selectedModule.status} />
              <StatusBadge
                value={
                  currentUser.isInternal ? "internal_user" : "client_approver"
                }
              />
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
              {selectedModule.description}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="module-menu-button lg:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label="Open module menu"
              onClick={onOpenMobileMenu}
            >
              <Menu className="h-4 w-4" />
            </button>
            <IconActionButton
              icon={RefreshCcw}
              label={refreshing ? "Refreshing data..." : "Refresh dashboard"}
              onClick={onRefresh}
            />
            <IconActionButton
              icon={LogOut}
              label="Log out"
              onClick={onLogout}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            label="Current Scope"
            value={selectedSite?.name ?? selectedSiteLabel}
            icon={Globe2}
          />
          <MetricTile
            label="Organizations"
            value={organizationCount}
            icon={FolderKanban}
          />
          <MetricTile
            label="Visible Opportunities"
            value={opportunityResults.length}
            icon={Sparkles}
          />
          <MetricTile label="Signed In" value={sessionEmail} icon={Activity} />
        </div>
      </header>
    );
  }

  if (activeTab === "dashboard") {
    return (
      <header className="glass-panel overflow-hidden p-5 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
              Autonomous SEO Ops
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-white sm:text-4xl">
                Approval, execution, and reporting
              </h1>
              <StatusBadge
                value={
                  currentUser.isInternal ? "internal_user" : "client_approver"
                }
              />
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
              The platform ingests search, analytics, paid media, and local SEO
              signals; turns them into ranked opportunities; and waits for
              approval before any change is executed.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="module-menu-button lg:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label="Open module menu"
              onClick={onOpenMobileMenu}
            >
              <Menu className="h-4 w-4" />
            </button>
            <IconActionButton
              icon={RefreshCcw}
              label={refreshing ? "Refreshing data..." : "Refresh dashboard"}
              onClick={onRefresh}
            />
            <IconActionButton
              icon={LogOut}
              label="Log out"
              onClick={onLogout}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            label="Opportunity Queue"
            value={scopedOpportunities.length}
            icon={FolderKanban}
          />
          <MetricTile
            label="Pending Approvals"
            value={pendingApprovalCount}
            icon={ShieldCheck}
          />
          <MetricTile
            label="Running Executions"
            value={runningExecutionCount + verifyingExecutionCount}
            icon={Play}
          />
          <MetricTile label="Signed In" value={sessionEmail} icon={Activity} />
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(18rem,0.75fr)]">
          <div className="module-spotlight">
            <div className="module-spotlight-icon">
              <SpotlightIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                  Module focus
                </p>
                {selectedModule ? (
                  <>
                    <StatusBadge value={selectedModule.kind} />
                    <StatusBadge value={selectedModule.status} />
                  </>
                ) : (
                  <StatusBadge value={activeTab} />
                )}
              </div>
              <p className="mt-3 text-lg font-semibold text-white">
                {selectedModule?.label ?? "Dashboard"}
              </p>
              <p className="mt-2 text-sm leading-6 text-neutral-400">
                {selectedModule?.description ??
                  "Use the dashboard to watch queue volume, scope, and execution posture across the workspace."}
              </p>
            </div>
          </div>

          <div className="surface-card bg-white/[0.03]">
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Workspace scope
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge value={selectedSite ? "single_site" : "all_sites"} />
              <StatusBadge value={`${organizationCount}_orgs`} />
            </div>
            <p className="mt-4 text-lg font-semibold text-white">
              {selectedSite?.name ?? selectedSiteLabel}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                  Projects in view
                </p>
                <p className="mt-2 text-2xl font-semibold text-white">
                  {projectCount}
                </p>
              </div>
              <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                  High-risk items
                </p>
                <p className="mt-2 text-2xl font-semibold text-white">
                  {highRiskOpportunityCount}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  if (activeTab === "opportunities") {
    return (
      <header className="glass-panel overflow-hidden p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
              Opportunity Queue
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-white">
                Ranked optimization work
              </h1>
              <StatusBadge
                value={
                  currentUser.isInternal ? "internal_user" : "client_approver"
                }
              />
              {selectedModule ? (
                <StatusBadge value={selectedModule.status} />
              ) : null}
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
              {selectedModule?.description ??
                "Review and prioritize the opportunity queue across content, metadata, performance, and structural work."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="module-menu-button lg:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label="Open module menu"
              onClick={onOpenMobileMenu}
            >
              <Menu className="h-4 w-4" />
            </button>
            <IconActionButton
              icon={RefreshCcw}
              label={refreshing ? "Refreshing data..." : "Refresh queue"}
              onClick={onRefresh}
            />
            <IconActionButton
              icon={LogOut}
              label="Log out"
              onClick={onLogout}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            label="Visible Opportunities"
            value={opportunityResults.length}
            icon={FolderKanban}
          />
          <MetricTile
            label="High Risk"
            value={highRiskOpportunityCount}
            icon={TriangleAlert}
          />
          <MetricTile
            label="Priority Score 40+"
            value={highScoreOpportunityCount}
            icon={Sparkles}
          />
          <MetricTile
            label="Current Scope"
            value={selectedSite?.name ?? selectedSiteLabel}
            icon={Globe2}
          />
        </div>
      </header>
    );
  }

  if (activeTab === "approvals") {
    return (
      <header className="glass-panel overflow-hidden p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
              Approval Queue
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-white">
                Review pending changes
              </h1>
              <StatusBadge
                value={
                  currentUser.isInternal ? "internal_user" : "client_approver"
                }
              />
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
              Route decision-ready action plans through the correct internal or
              client sign-off path before execution starts.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="module-menu-button lg:hidden"
              aria-expanded={mobileMenuOpen}
              aria-label="Open module menu"
              onClick={onOpenMobileMenu}
            >
              <Menu className="h-4 w-4" />
            </button>
            <IconActionButton
              icon={RefreshCcw}
              label={refreshing ? "Refreshing data..." : "Refresh approvals"}
              onClick={onRefresh}
            />
            <IconActionButton
              icon={LogOut}
              label="Log out"
              onClick={onLogout}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            label="Pending"
            value={pendingApprovalCount}
            icon={ShieldCheck}
          />
          <MetricTile
            label="Approved"
            value={approvedApprovalCount}
            icon={Check}
          />
          <MetricTile label="Rejected" value={rejectedApprovalCount} icon={X} />
          <MetricTile
            label="Current Scope"
            value={selectedSite?.name ?? selectedSiteLabel}
            icon={Globe2}
          />
        </div>
      </header>
    );
  }

  return (
    <header className="glass-panel overflow-hidden p-5 sm:p-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.26em] text-lime-300/80">
            Execution Runs
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold text-white">
              Monitor change delivery
            </h1>
            <StatusBadge
              value={
                currentUser.isInternal ? "internal_user" : "client_approver"
              }
            />
            {selectedModule ? (
              <StatusBadge value={selectedModule.provider} />
            ) : null}
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
            Track execution providers, retries, verification status, and failed
            runs from one operational queue.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="module-menu-button lg:hidden"
            aria-expanded={mobileMenuOpen}
            aria-label="Open module menu"
            onClick={onOpenMobileMenu}
          >
            <Menu className="h-4 w-4" />
          </button>
          <IconActionButton
            icon={RefreshCcw}
            label={refreshing ? "Refreshing data..." : "Refresh executions"}
            onClick={onRefresh}
          />
          <IconActionButton icon={LogOut} label="Log out" onClick={onLogout} />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Running" value={runningExecutionCount} icon={Play} />
        <MetricTile
          label="Verifying"
          value={verifyingExecutionCount}
          icon={ShieldCheck}
        />
        <MetricTile
          label="Failed"
          value={failedExecutionCount}
          icon={TriangleAlert}
        />
        <MetricTile
          label="Succeeded"
          value={succeededExecutionCount}
          icon={Check}
        />
      </div>
    </header>
  );
}

export function WorkspacePageContent({
  activeTab,
  currentUser,
  selectedModule,
  selectedSite,
  selectedSiteLabel,
  search,
  onSearchChange,
  assessmentTargetSite,
  assessmentSiteId,
  inventory,
  opportunityResults,
  scopedApprovals,
  approvalResults,
  scopedExecutions,
  executionResults,
  onRunSiteAssessment,
  onUpdateTargetAudience,
  onGenerateAction,
  onApprovalDecision,
  onRunExecution,
}: WorkspacePageContentProps) {
  const pendingApprovalCount = scopedApprovals.filter(
    (approval) => approval.status === "pending"
  ).length;
  const runningExecutionCount = scopedExecutions.filter(
    (execution) =>
      execution.status === "running" || execution.status === "verifying"
  ).length;
  const failedExecutionCount = scopedExecutions.filter(
    (execution) => execution.status === "failed"
  ).length;

  if (activeTab === "dashboard") {
    return (
      <div className="glass-panel rounded-tl-none p-5 sm:p-6">
        {selectedModule?.key === "target-audience" ? (
          <TargetAudienceEditor
            site={selectedSite}
            canEdit={currentUser.isInternal}
            onSave={onUpdateTargetAudience}
          />
        ) : (
          <InventoryDashboard inventory={inventory} />
        )}
      </div>
    );
  }

  if (activeTab === "opportunities") {
    return (
      <div className="glass-panel rounded-tl-none p-5 sm:p-6">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
          <div className="surface-card">
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Queue controls
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              Filter the ranked queue
            </p>
            <p className="mt-2 text-sm leading-6 text-neutral-400">
              Search by keyword, URL, provider, or summary to narrow the current
              queue without losing the active site scope.
            </p>
            <div className="mt-4">
              <QueueSearchField
                placeholder="Filter by keyword, URL, provider, or summary"
                value={search}
                onChange={onSearchChange}
              />
            </div>
          </div>

          <div className="surface-card bg-white/[0.03]">
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Active lane
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge value={selectedSite ? "single_site" : "all_sites"} />
              {selectedModule ? (
                <StatusBadge value={selectedModule.label} />
              ) : null}
            </div>
            <p className="mt-4 text-lg font-semibold text-white">
              {selectedSite?.name ?? selectedSiteLabel}
            </p>
            <p className="mt-2 text-sm leading-6 text-neutral-400">
              {selectedModule?.description ??
                "Browse every in-platform opportunity surface from one queue."}
            </p>
            {currentUser.isInternal ? (
              <button
                type="button"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-lime-300/25 bg-lime-300/10 px-4 py-3 text-sm font-medium text-lime-100 transition hover:bg-lime-300/15 disabled:opacity-50"
                disabled={
                  !assessmentTargetSite ||
                  assessmentSiteId === assessmentTargetSite.id
                }
                onClick={() =>
                  assessmentTargetSite
                    ? onRunSiteAssessment(assessmentTargetSite.id)
                    : undefined
                }
              >
                <Sparkles className="h-4 w-4" />
                {assessmentTargetSite &&
                assessmentSiteId === assessmentTargetSite.id
                  ? "Assessing site..."
                  : "Run site assessment"}
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {opportunityResults.length === 0 ? (
            <div className="surface-card text-sm text-neutral-400 xl:col-span-2">
              No opportunities match the current site and search filters.
            </div>
          ) : (
            opportunityResults.map((opportunity) => (
              <OpportunityCard
                key={opportunity.id}
                opportunity={opportunity}
                showActions={currentUser.isInternal}
                onGenerateAction={() => onGenerateAction(opportunity.id)}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  if (activeTab === "approvals") {
    return (
      <div className="glass-panel rounded-tl-none p-5 sm:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="surface-card xl:max-w-lg">
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Approval posture
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {pendingApprovalCount} item
              {pendingApprovalCount === 1 ? "" : "s"} waiting for a decision
            </p>
            <p className="mt-2 text-sm leading-6 text-neutral-400">
              Review the diff summary, requested role, and evidence snapshot
              before promoting work into the execution queue.
            </p>
          </div>

          <div className="w-full xl:max-w-xl">
            <QueueSearchField
              placeholder="Filter by stage, summary, or requested role"
              value={search}
              onChange={onSearchChange}
            />
          </div>
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {approvalResults.length === 0 ? (
            <div className="surface-card text-sm text-neutral-400 xl:col-span-2">
              No approvals match the current site and search filters.
            </div>
          ) : (
            approvalResults.map((approval) => (
              <ApprovalCard
                key={approval.id}
                approval={approval}
                canAct
                onApprove={() => onApprovalDecision(approval.id, "approve")}
                onReject={() => onApprovalDecision(approval.id, "reject")}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-tl-none p-5 sm:p-6">
      <div className="grid gap-4 xl:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)]">
        <div className="surface-card bg-white/[0.03]">
          <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
            Run status
          </p>
          <div className="mt-4 space-y-3">
            <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                Active runs
              </p>
              <p className="mt-2 text-2xl font-semibold text-white">
                {runningExecutionCount}
              </p>
            </div>
            <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                Failed runs
              </p>
              <p className="mt-2 text-2xl font-semibold text-white">
                {failedExecutionCount}
              </p>
            </div>
            <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                Current scope
              </p>
              <p className="mt-2 text-lg font-semibold text-white">
                {selectedSite?.name ?? selectedSiteLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <QueueSearchField
            placeholder="Filter by provider, status, or external run id"
            value={search}
            onChange={onSearchChange}
          />

          <div className="grid gap-4 xl:grid-cols-2">
            {executionResults.length === 0 ? (
              <div className="surface-card text-sm text-neutral-400 xl:col-span-2">
                No execution jobs match the current site and search filters.
              </div>
            ) : (
              executionResults.map((execution) => (
                <ExecutionCard
                  key={execution.id}
                  execution={execution}
                  showRun={currentUser.isInternal}
                  onRun={() => onRunExecution(execution.action_plan_id)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InventoryDashboard({ inventory }: { inventory: DashboardInventory }) {
  if (inventory.organizations.length === 0) {
    return (
      <div className="surface-card text-sm text-neutral-400">
        No organizations are available yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {inventory.organizations.map((organization) => (
        <article key={organization.id} className="surface-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                Organization
              </p>
              <h2 className="mt-2 text-xl font-semibold text-white">
                {organization.name}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge value={organization.kind} />
              <StatusBadge value={`${organization.projects.length}_projects`} />
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {organization.projects.length === 0 ? (
              <div className="rounded-2xl border border-white/6 bg-black/20 px-4 py-4 text-sm text-neutral-400">
                No projects are connected for this organization yet.
              </div>
            ) : (
              organization.projects.map((project) => (
                <section
                  key={project.id}
                  className="rounded-2xl border border-white/6 bg-black/20 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                        Project
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-white">
                        {project.name}
                      </h3>
                    </div>
                    <StatusBadge value={project.approval_mode} />
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    <InventoryBucket
                      title="Domains"
                      items={project.domains}
                      emptyLabel="No domains connected"
                    />
                    <InventoryBucket
                      title="Social Accounts"
                      items={project.social_accounts}
                      emptyLabel="No social accounts connected"
                    />
                    <InventoryBucket
                      title="Directory Listings"
                      items={project.directory_listings}
                      emptyLabel="No directory listings connected"
                    />
                  </div>
                </section>
              ))
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function InventoryBucket({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items: string[];
  emptyLabel: string;
}) {
  return (
    <div className="rounded-2xl border border-white/6 bg-white/[0.02] p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">
        {title}
      </p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500">{emptyLabel}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((item) => (
            <li key={item} className="text-sm text-white">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function OpportunityCard({
  opportunity,
  showActions,
  onGenerateAction,
}: {
  opportunity: Opportunity;
  showActions: boolean;
  onGenerateAction: () => void;
}) {
  const evidenceMetrics = Object.entries(
    (opportunity.evidence.metrics ?? {}) as Record<string, unknown>
  ).slice(0, 4);

  return (
    <article className="surface-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={opportunity.status} />
            <StatusBadge value={opportunity.type} />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-white">
            {opportunity.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-neutral-400">
            {opportunity.summary}
          </p>
        </div>
        {showActions ? (
          <IconActionButton
            icon={Sparkles}
            label="Generate action plan"
            onClick={onGenerateAction}
          />
        ) : null}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="min-w-0 rounded-2xl border border-white/6 bg-white/[0.02] p-4">
          <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
            Target surface
          </p>
          <p className="mt-2 break-words text-sm text-white [overflow-wrap:anywhere]">
            {opportunity.target_surface}
          </p>
        </div>
        <div className="min-w-0 rounded-2xl border border-white/6 bg-white/[0.02] p-4">
          <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
            Predicted impact
          </p>
          <p className="mt-2 text-sm text-white">
            Score {opportunity.score} · Risk {opportunity.risk_level}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {evidenceMetrics.map(([key, value]) => (
          <div
            key={key}
            className="rounded-2xl border border-white/6 bg-black/20 p-4"
          >
            <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
              {key.replace(/_/g, " ")}
            </p>
            <p className="mt-2 text-sm text-white">{String(value)}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function ApprovalCard({
  approval,
  canAct,
  onApprove,
  onReject,
}: {
  approval: Approval;
  canAct: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <article className="surface-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={approval.status} />
            <StatusBadge value={`${approval.stage}_approval`} />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-white">
            {approval.diff_summary}
          </h2>
          <p className="mt-2 text-sm leading-6 text-neutral-400">
            This approval requires{" "}
            {approval.requested_role ?? "an eligible user"} and keeps an
            immutable evidence snapshot.
          </p>
        </div>
        {canAct && approval.status === "pending" ? (
          <div className="flex items-center gap-2">
            <IconActionButton
              icon={Check}
              label="Approve"
              tone="accent"
              onClick={onApprove}
            />
            <IconActionButton
              icon={X}
              label="Reject"
              tone="danger"
              onClick={onReject}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}

function ExecutionCard({
  execution,
  showRun,
  onRun,
}: {
  execution: ExecutionJob;
  showRun: boolean;
  onRun: () => void;
}) {
  return (
    <article className="surface-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={execution.status} />
            <StatusBadge value={execution.provider} />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-white">
            {execution.provider.replace(/_/g, " ")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-neutral-400">
            Attempts {execution.attempts} · External run{" "}
            {execution.external_run_id ?? "pending"}.
          </p>
          {execution.last_error ? (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{execution.last_error}</span>
            </div>
          ) : null}
        </div>
        {showRun ? (
          <IconActionButton
            icon={Play}
            label="Run execution"
            onClick={onRun}
            disabled={
              execution.status === "running" || execution.status === "verifying"
            }
          />
        ) : null}
      </div>
    </article>
  );
}

export function App() {
  const [config, setConfig] = useState<BootstrapConfig | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(
    null
  );
  const [inventory, setInventory] = useState<DashboardInventory>({
    organizations: [],
  });
  const [dashboard, setDashboard] = useState<DashboardPayload>({
    opportunities: [],
    approvals: [],
    executions: [],
    sites: [],
  });
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      try {
        const initialConfig = await fetchBootstrap();
        if (!mounted) {
          return;
        }
        setConfig(initialConfig);
        const initialSession = await getInitialSession(initialConfig);
        if (!mounted) {
          return;
        }
        setSession(initialSession);

        const client = getBrowserSupabase(initialConfig);
        client.auth.onAuthStateChange((_event, nextSession) => {
          if (!mounted) {
            return;
          }
          setSession(nextSession);
        });
      } catch (bootstrapError) {
        setError(
          bootstrapError instanceof Error
            ? bootstrapError.message
            : "Bootstrap failed"
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  async function loadDashboard(activeSession: Session) {
    setRefreshing(true);
    setError(null);
    try {
      const [userPayload, sitesPayload, inventoryPayload, dashboardPayload] =
        await Promise.all([
          fetchAuthed<{ user: AuthenticatedUser }>(activeSession, "/api/me"),
          fetchAuthed<{ items: Site[] }>(activeSession, "/api/sites"),
          fetchAuthed<DashboardInventory>(activeSession, "/api/inventory"),
          fetchAuthed<
            Pick<DashboardPayload, "opportunities" | "approvals" | "executions">
          >(activeSession, "/api/dashboard"),
        ]);
      setCurrentUser(userPayload.user);
      setSites(sitesPayload.items);
      setInventory(inventoryPayload);
      setDashboard({
        ...dashboardPayload,
        sites: sitesPayload.items,
      });
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load dashboard"
      );
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    if (!session) {
      setCurrentUser(null);
      setInventory({
        organizations: [],
      });
      setDashboard({
        opportunities: [],
        approvals: [],
        executions: [],
        sites: [],
      });
      return;
    }

    void loadDashboard(session);
  }, [session]);

  async function refresh() {
    if (!session) {
      return;
    }
    await loadDashboard(session);
  }

  async function generateAction(opportunityId: string) {
    if (!session) {
      return;
    }
    await fetchAuthed(
      session,
      `/api/opportunities/${opportunityId}/generate-action`,
      {
        method: "POST",
      }
    );
    await refresh();
  }

  async function decision(approvalId: string, mode: "approve" | "reject") {
    if (!session) {
      return;
    }
    await fetchAuthed(session, `/api/approvals/${approvalId}/${mode}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    await refresh();
  }

  async function runExecution(actionPlanId: string) {
    if (!session) {
      return;
    }
    await fetchAuthed(session, `/api/executions/${actionPlanId}/run`, {
      method: "POST",
    });
    await refresh();
  }

  async function createSite(payload: {
    name: string;
    primaryUrl: string;
  }): Promise<Site> {
    if (!session) {
      throw new Error("You must be signed in to create a site");
    }

    const response = await fetchAuthed<{ item: Site }>(session, "/api/sites", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    await refresh();
    return response.item;
  }

  async function updateTargetAudience(
    siteId: string,
    targetAudience: SiteTargetAudience
  ) {
    if (!session) {
      throw new Error("You must be signed in to update target audience");
    }

    await fetchAuthed<{ item: Site }>(
      session,
      `/api/sites/${siteId}/target-audience`,
      {
        method: "PUT",
        body: JSON.stringify({
          targetAudience,
        }),
      }
    );

    await refresh();
  }

  async function runSiteAssessment(siteId: string) {
    if (!session) {
      throw new Error("You must be signed in to assess a site");
    }

    await fetchAuthed(session, `/api/sites/${siteId}/run-assessment`, {
      method: "POST",
    });

    for (let attempt = 0; attempt < 4; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 3_000));
      await refresh();
    }
  }

  async function logout() {
    if (!config) {
      return;
    }
    const client = getBrowserSupabase(config);
    await client.auth.signOut();
    setSession(null);
  }

  async function prepareKeywordDensityLaunch(siteId: string | null) {
    if (!session) {
      throw new Error("You must be signed in to open Keyword Density");
    }

    return fetchAuthed<{
      launchUrl: string;
      targetOrigin: string;
      expiresAt: string;
    }>(session, "/api/tool-handoffs/keyword-density", {
      method: "POST",
      body: JSON.stringify({
        siteId,
      }),
    });
  }

  if (loading) {
    return (
      <div className="app-shell items-center justify-center">
        <div className="glass-panel w-full max-w-lg p-8 text-center text-sm text-neutral-400">
          Loading bootstrap configuration…
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="app-shell items-center justify-center">
        <div className="glass-panel w-full max-w-lg p-8 text-center text-sm text-red-100">
          {error ?? "The app bootstrap configuration is missing."}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <AuthView
          config={config}
          onAuthenticated={(nextSession) => setSession(nextSession)}
        />
        {error ? (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}
      </>
    );
  }

  if (!currentUser) {
    return (
      <div className="app-shell items-center justify-center">
        <div className="glass-panel w-full max-w-lg p-8 text-center text-sm text-neutral-400">
          Loading your memberships and dashboard data…
        </div>
      </div>
    );
  }

  return (
    <>
      <AppShell
        session={session}
        currentUser={currentUser}
        sites={sites}
        inventory={inventory}
        dashboard={dashboard}
        refreshing={refreshing}
        onRefresh={refresh}
        onCreateSite={createSite}
        onRunSiteAssessment={runSiteAssessment}
        onUpdateTargetAudience={updateTargetAudience}
        onGenerateAction={generateAction}
        onApprovalDecision={decision}
        onRunExecution={runExecution}
        onPrepareKeywordDensityLaunch={prepareKeywordDensityLaunch}
        onLogout={logout}
      />
      {error ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      ) : null}
    </>
  );
}
